// components/leads/LeadPhasesBar.js
// Barra fasi orizzontale per visualizzare lo stato del lead
// Componente SOLO VISUALE - non modifica stati

import React from 'react';
import { Check, Circle, X, Trophy } from 'lucide-react';
import { formatDate } from '../../lib/utils';

// Ordine fasi del workflow lead (esclude stati terminali negativi)
const WORKFLOW_PHASES = [
  { status: 'nuovo', label: 'Nuovo', shortLabel: 'Nuovo' },
  { status: 'contattato', label: 'Contattato', shortLabel: 'Contattato' },
  { status: 'assegnato', label: 'Assegnato', shortLabel: 'Assegnato' },
  { status: 'appuntamento_fissato', label: 'Appuntamento', shortLabel: 'Appunt.' },
  { status: 'sopralluogo_effettuato', label: 'Sopralluogo', shortLabel: 'Sopral.' },
  { status: 'in_preventivazione', label: 'Preventivazione', shortLabel: 'Prev.' },
  { status: 'preventivo_presentato', label: 'Presentato', shortLabel: 'Pres.' },
  { status: 'in_trattativa', label: 'Trattativa', shortLabel: 'Tratt.' },
  { status: 'in_contratto', label: 'Contratto', shortLabel: 'Contr.' },
  { status: 'convertito', label: 'Convertito', shortLabel: 'Vinto' },
];

// Stati fuori dal workflow principale
const SPECIAL_STATES = {
  perso: { label: 'Perso', color: 'bg-red-500', textColor: 'text-red-600' },
  non_risponde: { label: 'Non risponde', color: 'bg-amber-500', textColor: 'text-amber-600' },
};

/**
 * LeadPhasesBar - Barra fasi orizzontale stile Teamleader
 * 
 * @param {string} currentStatus - Stato corrente del lead
 * @param {Array} transitions - Array opzionale di transizioni [{from_status, to_status, created_at}]
 * @param {string} createdAt - Data creazione lead
 */
const LeadPhasesBar = ({ currentStatus, transitions = [], createdAt }) => {
  // Trova l'indice della fase corrente
  const currentPhaseIndex = WORKFLOW_PHASES.findIndex(p => p.status === currentStatus);
  
  // Se lo stato è speciale (perso, non_risponde), mostralo separatamente
  const isSpecialState = SPECIAL_STATES[currentStatus];
  
  // Costruisci mappa date transizioni per mostrare sotto le fasi
  const transitionDates = {};
  if (transitions && transitions.length > 0) {
    transitions.forEach(t => {
      if (t.to_status && t.created_at) {
        transitionDates[t.to_status] = t.created_at;
      }
    });
  }

  // Se non c'è data transizione per lo stato corrente, usa createdAt
  if (!transitionDates[currentStatus] && createdAt) {
    transitionDates[currentStatus] = createdAt;
  }

  return (
    <div className="w-full" data-testid="lead-phases-bar">
      {/* Stato speciale (perso/non_risponde) - Banner separato */}
      {isSpecialState && (
        <div className={`mb-4 px-4 py-2 rounded-lg ${isSpecialState.color} bg-opacity-10 border border-current ${isSpecialState.textColor}`}>
          <div className="flex items-center gap-2">
            <X className="w-4 h-4" />
            <span className="font-medium">{isSpecialState.label}</span>
            {transitionDates[currentStatus] && (
              <span className="text-sm opacity-75">
                - {formatDate(transitionDates[currentStatus])}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Barra fasi principale */}
      <div className="relative">
        {/* Container scrollabile per mobile */}
        <div className="overflow-x-auto pb-2 -mx-2 px-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
          <div className="flex items-start min-w-max gap-0">
            {WORKFLOW_PHASES.map((phase, index) => {
              const isCompleted = currentPhaseIndex > index;
              const isCurrent = currentPhaseIndex === index && !isSpecialState;
              const isFuture = currentPhaseIndex < index || (isSpecialState && currentPhaseIndex === -1);
              const isLast = index === WORKFLOW_PHASES.length - 1;
              const isWon = phase.status === 'convertito' && isCurrent;

              return (
                <div 
                  key={phase.status} 
                  className="flex items-start"
                  data-testid={`phase-${phase.status}`}
                >
                  {/* Fase */}
                  <div className="flex flex-col items-center min-w-[60px] sm:min-w-[80px]">
                    {/* Cerchio fase */}
                    <div 
                      className={`
                        w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center 
                        transition-all duration-300 relative z-10
                        ${isCompleted 
                          ? 'bg-[#FF7819] text-white' 
                          : isCurrent 
                            ? isWon 
                              ? 'bg-green-500 text-white ring-4 ring-green-200' 
                              : 'bg-[#FF7819] text-white ring-4 ring-orange-200'
                            : 'bg-gray-200 text-gray-400'
                        }
                      `}
                    >
                      {isCompleted ? (
                        <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      ) : isWon ? (
                        <Trophy className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      ) : (
                        <Circle className="w-2.5 h-2.5 sm:w-3 sm:h-3" fill={isCurrent ? 'currentColor' : 'none'} />
                      )}
                    </div>

                    {/* Label fase */}
                    <div className="mt-1.5 sm:mt-2 text-center px-1">
                      <p 
                        className={`
                          text-[10px] sm:text-xs leading-tight whitespace-nowrap
                          ${isCurrent 
                            ? 'font-semibold text-[#3C3C3C]' 
                            : isCompleted 
                              ? 'font-medium text-[#737373]' 
                              : 'text-gray-400'
                          }
                        `}
                      >
                        {phase.shortLabel}
                      </p>
                      
                      {/* Data transizione (se disponibile) */}
                      {transitionDates[phase.status] && (isCompleted || isCurrent) && (
                        <p className="text-[9px] sm:text-[10px] text-[#999] mt-0.5 whitespace-nowrap">
                          {formatDate(transitionDates[phase.status])}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Linea connettore */}
                  {!isLast && (
                    <div className="flex items-center h-7 sm:h-8 -mx-1">
                      <div 
                        className={`
                          w-4 sm:w-8 h-0.5 sm:h-1 rounded-full transition-all duration-300
                          ${isCompleted 
                            ? 'bg-[#FF7819]' 
                            : 'bg-gray-200'
                          }
                        `}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Indicatore scroll mobile (gradient fade) - lato destro */}
        <div className="absolute right-0 top-0 h-10 w-6 bg-gradient-to-l from-white to-transparent pointer-events-none sm:hidden" />
        {/* Indicatore scroll mobile (gradient fade) - lato sinistro */}
        <div className="absolute left-0 top-0 h-10 w-6 bg-gradient-to-r from-white to-transparent pointer-events-none sm:hidden" />
      </div>
    </div>
  );
};

export default LeadPhasesBar;
